import { useCallback, useEffect, useState } from "react";
import { Crown, Play, Zap, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { LogoAvatar, ScoreDonut } from "@/components/SignalBadges";

export default function HeroBoardPreview({ companies = [], primeCompany = null, homeVideoCompanies = [] }) {
  const rows = companies.slice(0, 3);
  const [activeIdx, setActiveIdx] = useState(0);

  // Reset to first slot whenever the playlist length changes
  useEffect(() => { setActiveIdx(0); }, [homeVideoCompanies.length]);

  const activeVideoCompany = homeVideoCompanies.length > 0 ? homeVideoCompanies[activeIdx] : null;

  const handleVideoEnded = useCallback(() => {
    if (homeVideoCompanies.length > 1) {
      setActiveIdx((i) => (i + 1) % homeVideoCompanies.length);
    }
  }, [homeVideoCompanies.length]);

  // Priority: Pro Boost home screen slot → Prime Spot company → first company with any media
  const mediaCompany =
    activeVideoCompany ||
    primeCompany ||
    companies.find((c) => c.slot_video_url || c.gif_url || c.logo_url);

  // Home screen VIDEO is a Pro Boost-only perk — uses its own dedicated URL
  const isHomeScreenVideo = Boolean(activeVideoCompany);
  const mediaVideoUrl = isHomeScreenVideo ? (activeVideoCompany.home_video_url || activeVideoCompany.slot_video_url) : null;
  const mediaVideoWebm = isHomeScreenVideo ? activeVideoCompany.slot_video_webm : null;

  return (
    <aside
      data-testid="hero-board-preview"
      className="hero-board-preview relative rounded-[1.75rem] border border-emerald-400/25 bg-[#0E1712]/95 p-3 shadow-[0_24px_70px_rgba(0,0,0,0.30),0_0_45px_rgba(63,224,140,0.16)] overflow-hidden"
      aria-label="HighRank leaderboard preview"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(63,224,140,0.16),transparent_34%),linear-gradient(145deg,rgba(255,255,255,0.05),transparent_45%)] pointer-events-none" />
      <div className="hero-board-inner relative rounded-[1.35rem] border border-white/10 bg-[#111813]/95 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div>
            <p className="font-mono2 text-[10px] uppercase tracking-[0.28em] text-emerald-400">Live leaderboard</p>
            <p className="text-xs text-slate-400 mt-0.5">Rank, reputation and featured media</p>
          </div>
          {primeCompany ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 font-mono2 text-[9px] uppercase tracking-widest text-amber-400">
              <Crown size={9} /> Prime Spot
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 font-mono2 text-[9px] uppercase tracking-widest text-amber-400">
              <Zap size={10} /> Top Spot
            </span>
          )}
        </div>

        <div className="p-4">
          {/* Featured media slot — always the prime company when one is active */}
          <div
            className={`relative aspect-video overflow-hidden rounded-xl border bg-black shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)] ${
              primeCompany ? "border-amber-500/30" : "border-emerald-400/25"
            }`}
            data-testid="hero-board-media"
          >
            {mediaVideoUrl ? (
              <video
                key={activeIdx}
                autoPlay
                muted
                playsInline
                loop={homeVideoCompanies.length <= 1}
                preload="metadata"
                onEnded={handleVideoEnded}
                className="h-full w-full object-cover animate-[fadeIn_0.6s_ease]"
                style={{ animationFillMode: "both" }}
              >
                {mediaVideoWebm && <source src={mediaVideoWebm} type="video/webm" />}
                <source src={mediaVideoUrl} type="video/mp4" />
              </video>
            ) : mediaCompany?.gif_url || mediaCompany?.logo_url ? (
              <img
                src={mediaCompany.gif_url || mediaCompany.logo_url}
                alt={`${mediaCompany.name} featured media`}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-[radial-gradient(circle_at_30%_20%,rgba(63,224,140,0.16),transparent_45%)]">
                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-emerald-400/30 bg-emerald-400/10">
                  <Play size={16} className="text-emerald-300" />
                </span>
                <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-400">Featured media slot</span>
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
            {mediaCompany && (
              <div className="absolute bottom-3 left-3 flex items-center gap-2 rounded-full border border-white/10 bg-black/55 px-2.5 py-1 backdrop-blur-sm">
                <LogoAvatar name={mediaCompany.name} logoUrl={mediaCompany.logo_url} size={22} testId="hero-board-media-avatar" />
                <span className="font-mono2 text-[10px] uppercase tracking-widest text-white">{mediaCompany.name}</span>
                {isHomeScreenVideo && (
                  <span className="font-mono2 text-[8px] uppercase tracking-widest text-amber-400 border border-amber-500/40 bg-amber-500/10 rounded px-1.5 py-0.5">Pro</span>
                )}
                {!isHomeScreenVideo && primeCompany && mediaCompany.id === primeCompany.id && (
                  <Crown size={9} className="text-amber-400" />
                )}
              </div>
            )}
            {/* Dot indicators — only shown when 2+ videos are in the playlist */}
            {homeVideoCompanies.length > 1 && (
              <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
                {homeVideoCompanies.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIdx(i)}
                    aria-label={`Switch to video ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === activeIdx
                        ? "w-4 bg-emerald-400"
                        : "w-1.5 bg-white/30 hover:bg-white/50"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 space-y-2">
            {/* If prime company exists and isn't in top-3, inject it as first row */}
            {primeCompany && !rows.find((c) => c.id === primeCompany.id) && (
              <Link
                to={`/company/${primeCompany.slug}`}
                data-testid={`hero-board-row-${primeCompany.slug}`}
                className="flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2.5 transition-colors duration-200 hover:bg-amber-500/[0.12]"
              >
                <span className="w-7 shrink-0 font-mono2 text-sm font-bold text-amber-400">
                  <Crown size={13} className="text-amber-400" />
                </span>
                <LogoAvatar name={primeCompany.name} logoUrl={primeCompany.logo_url} size={30} testId={`hero-board-avatar-prime`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-heading text-sm font-bold text-slate-100">{primeCompany.name}</p>
                  <p className="truncate font-mono2 text-[9px] uppercase tracking-widest text-amber-500/70">
                    Prime Spot · {primeCompany.category}
                  </p>
                </div>
                <ScoreDonut score={primeCompany.highrank_score} size={36} stroke={4} testId={`hero-board-score-prime`} />
              </Link>
            )}

            {rows.map((company, index) => (
              <Link
                to={`/company/${company.slug}`}
                key={company.id}
                data-testid={`hero-board-row-${company.slug}`}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors duration-200 hover:bg-white/[0.08] ${
                  company.id === primeCompany?.id
                    ? "border-amber-500/30 bg-amber-500/[0.07]"
                    : index === 0
                    ? "border-emerald-400/30 bg-emerald-400/[0.09]"
                    : "border-white/10 bg-white/[0.055]"
                }`}
              >
                <span className="w-7 shrink-0 font-mono2 text-sm font-bold text-emerald-400">
                  {company.id === primeCompany?.id ? (
                    <Crown size={13} className="text-amber-400" />
                  ) : (
                    `#${index + 1}`
                  )}
                </span>
                <LogoAvatar name={company.name} logoUrl={company.logo_url} size={30} testId={`hero-board-avatar-${company.slug}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-heading text-sm font-bold text-slate-100">{company.name}</p>
                  <p className="truncate font-mono2 text-[9px] uppercase tracking-widest text-slate-400">
                    {company.category} · {company.region}
                    {company.views > 0 && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 text-slate-400">
                        <Eye size={8} /> {company.views}
                      </span>
                    )}
                  </p>
                </div>
                <ScoreDonut score={company.highrank_score} size={36} stroke={4} testId={`hero-board-score-${company.slug}`} />
              </Link>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3">
            <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-400">Boost stays separate</span>
            <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-emerald-300">Score earns trust</span>
          </div>
        </div>
      </div>
    </aside>
  );
}
